/// Per-key field rules for the generic グローバル設定 editor (SYS-map-provider).
/// The screen renders whatever `system_settings` rows the API returns, so anything a
/// SPECIFIC entry needs (masked input, format rule) is declared here instead of being
/// branched inline in the page — a new entry only touches this file.

/** Which map provider the Web uses. */
export const MAP_PROVIDER_SETTING = 'default_map_provider'
/** Value of MAP_PROVIDER_SETTING that selects Google Maps. */
export const MAP_PROVIDER_GOOGLE = 'google_map'
/** Google Maps key the Web map itself uses (read at runtime from client-config). */
export const GOOGLE_MAPS_API_KEY_WEB = 'google_maps_api_key_web'

/** Setting keys holding an API key of another system → masked input, never shown plain. */
const SECRET_SETTING_KEYS = new Set([GOOGLE_MAPS_API_KEY_WEB])

/**
 * Google Maps API key: `AIza` + 35 URL-safe base64 chars.
 * MIRROR of the backend rule (`GOOGLE_MAPS_API_KEY_PATTERN` in
 * `NodeJS/src/modules/system-setting/system-setting.secrets.ts`) — keep both in sync.
 * The backend is the authority; this copy only exists to fail fast in the form.
 */
export const GOOGLE_MAPS_API_KEY_PATTERN = /^AIza[0-9A-Za-z_-]{35}$/

const PATTERN_BY_KEY: Record<string, RegExp> = {
  [GOOGLE_MAPS_API_KEY_WEB]: GOOGLE_MAPS_API_KEY_PATTERN,
}

/** Whether the entry is a credential (render it masked, with a reveal toggle). */
export function isSecretSetting(key: string): boolean {
  return SECRET_SETTING_KEYS.has(key)
}

/** Format rule for the entry, or `null` when it has none. */
export function settingPattern(key: string): RegExp | null {
  return PATTERN_BY_KEY[key] ?? null
}

/**
 * Whether the drafted value breaks the entry's format rule (gates the Save button).
 * Empty stays VALID: clearing an API key is the deliberate "disable it" action —
 * for the Web key that means the map falls back to OpenStreetMap.
 */
export function isSettingValueInvalid(key: string, value: string): boolean {
  const pattern = settingPattern(key)
  const trimmed = value.trim()
  return !!pattern && trimmed !== '' && !pattern.test(trimmed)
}

/**
 * Backend check code → i18n key. Codes are the cross-layer contract (CLAUDE.COMMON §1.2):
 * the backend never sends display text, the screen localizes by code. A code missing here
 * falls back to showing the raw code + Google's `detail`, which is still readable — better
 * than hiding a finding because the catalog lagged behind.
 */
export const GMAPS_CHECK_I18N: Record<string, string> = {
  GMAPS_KEY_FORMAT_INVALID: 'gmapsCheckFormatInvalid',
  GMAPS_KEY_INVALID: 'gmapsCheckKeyInvalid',
  GMAPS_KEY_VALID: 'gmapsCheckKeyValid',
  GMAPS_BILLING_DISABLED: 'gmapsCheckBillingDisabled',
  GMAPS_KEY_API_RESTRICTED: 'gmapsCheckKeyApiRestricted',
  GMAPS_ORIGIN_ALLOWED: 'gmapsCheckOriginAllowed',
  GMAPS_ORIGIN_BLOCKED: 'gmapsCheckOriginBlocked',
  GMAPS_UNCLASSIFIED_RESPONSE: 'gmapsCheckUnclassified',
  GMAPS_RESTRICTION_REFERRER: 'gmapsCheckRestrictionReferrer',
  GMAPS_RESTRICTION_NOT_REFERRER: 'gmapsCheckRestrictionNotReferrer',
  GMAPS_RESTRICTION_NONE: 'gmapsCheckRestrictionNone',
  GMAPS_RESTRICTION_UNKNOWN: 'gmapsCheckRestrictionUnknown',
  GMAPS_JS_NOT_VERIFIABLE: 'gmapsCheckJsNotVerifiable',
  GMAPS_PROBE_UNREACHABLE: 'gmapsCheckUnreachable',
}
